<template>
  <component
    :is="Button"
    full-width="adaptive"
    type="submit"
    variant="primary"
    v-bind="buttonProps"
  >
    <slot>
      {{ i18n(translations.submitButton) }}
    </slot>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'

import type { PaymentFormInstance } from '../../../form-common/types/PaymentFormInstance'

import { getPaymentSubmitButtonDataQAAttributes } from './PaymentSubmitButton.helpers'
import translations from './PaymentSubmitButton.translations'

const i18n = useI18n()

const props = defineProps<{
  /**
   * The reference to the form instance.
   */
  form: PaymentFormInstance | null
}>()

function usePaymentSubmitButton() {
  const methodFormInstance = () => props.form?.methodFormInstance
  const adapterInstance = () => methodFormInstance()?.adapterInstance

  const adapterProps = computed(
    () => adapterInstance()?.submitButton?.props ?? {},
  )
  const formId = computed(() => methodFormInstance()?.formId)

  const isBusy = computed(
    () => methodFormInstance()?.isBusy ?? props.form?.isFormBusy ?? false,
  )
  const isDisabled = computed(
    () =>
      (isBusy.value || !props.form || methodFormInstance()?.isDisabled) ??
      false,
  )

  const buttonProps = computed(() => ({
    ...adapterProps.value,
    form: formId.value,
    disabled: isDisabled.value,
    loading: isBusy.value,
    ...getPaymentSubmitButtonDataQAAttributes(
      methodFormInstance()?.method ?? null,
    ),
  }))

  return {
    Button: computed(() => adapterInstance()?.submitButton?.is ?? RevButton),
    buttonProps,
  }
}

const { Button, buttonProps } = usePaymentSubmitButton()
</script>
